/**
 * T147 — /suppliers list page.
 *
 * Sprint 006 (A3): vollständig verdrahtet — q/page/includeArchived als
 * URL-State (validateSearch), debounced Suche, Pagination, PageHeader +
 * StatusBadge + Kanon-Palette. Vorher: lokaler State ohne Suche/Paging,
 * Status als Plain-Text.
 */
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { z } from 'zod';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { AppShell } from '../components/layout/AppShell';
import {
  Button,
  EmptyState,
  FilterPill,
  GlassPanel,
  Input,
  LoadingSpinner,
  Modal,
  PageHeader,
  Pagination,
  SearchInput,
  StatusBadge,
} from '../components/ui';
import { useSuppliers, useCreateSupplier } from '../services/api/suppliers';
import { useToastHelpers } from '../components/ui/Toast';
import { useUnsavedChanges, markFormSaved } from '../hooks/useUnsavedChanges';
import { apiErrorMessage } from '../lib/api-error-message';

const suppliersSearchSchema = z.object({
  page: z.coerce.number().int().positive().optional().default(1),
  q: z.string().optional(),
  includeArchived: z
    .preprocess((value) => value === 'true' || value === true, z.boolean())
    .optional(),
});

type SuppliersSearch = z.infer<typeof suppliersSearchSchema>;

export const Route = createFileRoute('/suppliers')({
  component: SuppliersPage,
  validateSearch: suppliersSearchSchema,
});

function SuppliersPage() {
  return (
    <ProtectedRoute>
      <AppShell>
        <SuppliersInner />
      </AppShell>
    </ProtectedRoute>
  );
}

function SuppliersInner() {
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();
  const { data, isLoading } = useSuppliers({
    page: search.page,
    q: search.q || undefined,
    includeArchived: search.includeArchived ?? false,
  });
  const create = useCreateSupplier();
  const toast = useToastHelpers();
  const rowNavigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [confirmCloseForm, setConfirmCloseForm] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [vatId, setVatId] = useState('');
  const [kleinunternehmer, setKleinunternehmer] = useState(false);
  const createDraft = useUnsavedChanges(
    { displayName, vatId, kleinunternehmer },
    undefined,
    showForm
  );
  const createResetPending = useRef(false);
  useEffect(() => {
    if (!createResetPending.current || showForm) return;
    createDraft.markSaved();
    createResetPending.current = false;
  }, [createDraft.markSaved, displayName, kleinunternehmer, showForm, vatId]);

  const updateSearch = (patch: Partial<SuppliersSearch>) => {
    navigate({
      search: (prev: SuppliersSearch) => ({ ...prev, ...patch }),
      replace: true,
    });
  };

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      await create.mutateAsync({
        displayName,
        vatId: vatId || null,
        additionalAllowedEmails: [],
        kleinunternehmer,
      });
      markFormSaved();
      toast.success('Lieferant angelegt');
      resetCreateDraft();
      setShowForm(false);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Lieferant konnte nicht angelegt werden.'));
    }
  }

  function resetCreateDraft() {
    createResetPending.current = true;
    setDisplayName('');
    setVatId('');
    setKleinunternehmer(false);
  }

  function toggleCreateForm() {
    if (showForm) {
      if (createDraft.isDirty) {
        setConfirmCloseForm(true);
        return;
      }
      resetCreateDraft();
      setShowForm(false);
    } else {
      setShowForm(true);
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Lieferanten"
        subtitle={`${data?.pagination.total ?? 0} Lieferanten`}
        actions={
          <Button onClick={toggleCreateForm} disabled={create.isPending}>
            {showForm ? 'Abbrechen' : '+ Lieferant'}
          </Button>
        }
      />

      <GlassPanel className="mb-6">
        <div className="flex flex-col sm:flex-row gap-4 sm:items-center">
          <div className="flex-1">
            <SearchInput
              value={search.q ?? ''}
              onChange={(q) => updateSearch({ q: q || undefined, page: 1 })}
              placeholder="Suchen (Name, USt-ID)…"
              data-testid="supplier-search"
            />
          </div>
          <FilterPill
            active={!!search.includeArchived}
            onClick={() =>
              updateSearch({ includeArchived: search.includeArchived ? undefined : true, page: 1 })
            }
            data-testid="supplier-list-show-archived"
          >
            Archiv anzeigen
          </FilterPill>
        </div>
      </GlassPanel>

      {showForm && (
        // 006b P4: GlassPanel statt naked box (Kanon §6)
        <GlassPanel className="mb-6">
          <form onSubmit={onSubmit}>
            <fieldset disabled={create.isPending} className="space-y-4 border-0 p-0 m-0 min-w-0">
              <Input
                label="Name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                required
              />
              <Input
                label="USt-ID"
                value={vatId}
                onChange={(e) => setVatId(e.target.value)}
                placeholder="DE123456789"
              />
              <label className="flex items-center gap-2 text-white/80">
                <input
                  type="checkbox"
                  checked={kleinunternehmer}
                  onChange={(e) => setKleinunternehmer(e.target.checked)}
                />
                Kleinunternehmer (§19 UStG — keine MwSt auf Rechnung)
              </label>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? 'Speichern…' : 'Speichern'}
              </Button>
            </fieldset>
          </form>
        </GlassPanel>
      )}

      <Modal
        isOpen={confirmCloseForm}
        onClose={() => {
          if (!create.isPending) setConfirmCloseForm(false);
        }}
        title="Änderungen verwerfen?"
      >
        <p className="text-white/70 mb-6">Ungespeicherte Lieferantendaten gehen verloren.</p>
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setConfirmCloseForm(false)}
            disabled={create.isPending}
          >
            Weiter bearbeiten
          </Button>
          <Button
            type="button"
            variant="danger"
            disabled={create.isPending}
            onClick={() => {
              setConfirmCloseForm(false);
              resetCreateDraft();
              setShowForm(false);
            }}
          >
            Änderungen verwerfen
          </Button>
        </div>
      </Modal>

      {isLoading && <LoadingSpinner />}

      {data && data.items.length === 0 && (
        <EmptyState
          title={search.q ? 'Keine Treffer' : 'Keine Lieferanten'}
          description={
            search.q
              ? 'Suche anpassen oder Filter zurücksetzen.'
              : 'Lieferanten werden automatisch beim ersten Beleg oder manuell erfasst.'
          }
        />
      )}

      {data && data.items.length > 0 && (
        <GlassPanel
          variant="subtle"
          padding="none"
          className="overflow-hidden"
          data-testid="suppliers-list-surface"
        >
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-white/10">
              <thead>
                <tr className="text-left text-xs font-medium text-white/40 uppercase tracking-wider">
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3 hidden md:table-cell">USt-ID</th>
                  <th className="px-4 py-3 hidden lg:table-cell">§19</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {data.items.map((s) => (
                  <tr
                    key={s.id}
                    className={`hover:bg-white/5 cursor-pointer ${s.isArchived ? 'opacity-60' : ''}`}
                    onClick={() => rowNavigate({ to: '/suppliers/$id', params: { id: s.id } })}
                  >
                    <td className="px-4 py-3 text-white">{s.displayName}</td>
                    <td className="px-4 py-3 text-white/70 font-mono text-sm hidden md:table-cell">
                      {s.vatId ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-white/70 hidden lg:table-cell">
                      {s.kleinunternehmer ? 'Ja' : 'Nein'}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge tone={s.isArchived ? 'muted' : 'success'}>
                        {s.isArchived ? 'Archiviert' : 'Aktiv'}
                      </StatusBadge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </GlassPanel>
      )}

      {data && data.pagination.totalPages > 1 && (
        <div className="flex justify-center">
          <Pagination
            page={search.page}
            totalPages={data.pagination.totalPages}
            onPageChange={(page) => updateSearch({ page })}
          />
        </div>
      )}
    </div>
  );
}
